import { createSignal, Show } from "solid-js";
import type { Provider } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

interface AuthScreenProps {
  offlineSetup?: boolean;
}

export function AuthScreen(props: AuthScreenProps) {
  const [email, setEmail] = createSignal("");
  const [sent, setSent] = createSignal(false);
  const [loading, setLoading] = createSignal<Provider | "email" | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  async function signInWithProvider(provider: Extract<Provider, "google" | "github">) {
    if (!supabase || loading()) return;
    setLoading(provider);
    setError(null);
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (authError) {
      setError(authError.message);
      setLoading(null);
    }
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (!supabase || loading()) return;
    const value = email().trim().toLowerCase();
    if (!value) return;
    setLoading("email");
    setError(null);
    const { error: authError } = await supabase.auth.signInWithOtp({
      email: value,
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(null);
    if (authError) setError(authError.message);
    else setSent(true);
  }

  return (
    <main class="auth-shell">
      <section class="auth-card" aria-labelledby="auth-title">
        <p class="eyebrow">Quiet Todos</p>
        <h1 id="auth-title">A calm, local-first place for the day.</h1>
        <p class="muted">Fast keyboard-first tasks with background Supabase sync, projects, due dates, and offline support.</p>

        <Show
          when={!props.offlineSetup}
          fallback={
            <div class="notice error" role="alert">
              Add <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> to enable sign-in.
            </div>
          }
        >
          <div class="auth-providers" aria-label="Sign-in options">
            <button
              class="oauth-button"
              type="button"
              disabled={Boolean(loading())}
              onClick={() => void signInWithProvider("google")}
            >
              <span class="provider-mark" aria-hidden="true">G</span>
              {loading() === "google" ? "Opening Google..." : "Continue with Google"}
            </button>
            <button
              class="oauth-button"
              type="button"
              disabled={Boolean(loading())}
              onClick={() => void signInWithProvider("github")}
            >
              <span class="provider-mark provider-mark-dark" aria-hidden="true">GH</span>
              {loading() === "github" ? "Opening GitHub..." : "Continue with GitHub"}
            </button>
          </div>

          <details class="email-fallback">
            <summary>Use email instead</summary>
            <form class="auth-form" onSubmit={submit}>
              <label for="email">Email</label>
              <div class="inline-field">
                <input
                  id="email"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  required
                  value={email()}
                  onInput={(event) => setEmail(event.currentTarget.value)}
                  placeholder="you@example.com"
                />
                <button type="submit" disabled={Boolean(loading())}>
                  {loading() === "email" ? "Sending..." : "Send link"}
                </button>
              </div>
            </form>
          </details>
          <Show when={sent()}>
            <p class="notice success" role="status">Check your email for a magic link.</p>
          </Show>
          <Show when={error()}>
            <p class="notice error" role="alert">{error()}</p>
          </Show>
        </Show>
      </section>
    </main>
  );
}
